import { computed } from 'vue'
import { useStorage } from '@vueuse/core'
import { useTabStorage } from '@/composables/useTabSpecificStorage'
import EntitiesDataService from '@/core/services/cachedData/EntitiesDataService'
import GlobalCachedDataService from '@/core/services/cachedData/GlobalCachedDataService'
import { redirectToLogin } from '@/composables/useCurrentUserData'
import {
    AccountBreak,
    CampaignEntity,
    EntityListData,
    ExtensionEntity,
    QueueEntity, UserEntity,
    UserRecord,
    VoiceBotEntity
} from '@/types/entities'
import statusTypes from '@/enum/redesignStatusTypes'
import uniqBy from 'lodash/uniqBy'
import Logger from '@/core/services/Logger'
import { entitiesApi } from '@/api/entitiesApi'
import IndexDatabaseService from '@/core/services/idb/IndexDatabaseService'
import SelectedAccountDataEntitiesService from '@/core/services/cachedData/SelectedAccountDataEntitiesService'
import { ActiveDashboardDataConfig } from '@/composables/globalData/useDashoardsData'
import { ENTITIES_CONFIG } from '@/enum/etitiesConfig'

const logger = Logger.makeSectionLogger('USE_ENTITIES_DATA')

/* Constants */
const ENTITIES_STORE_KEY = 'EntitiesData'
const DEFAULT_EXPIRATION_CACHE = 1000 * 60 * 60 * 2 // 2 hours

/* Types */
type EntitiesStoreType = {
    selectedAccountID: number | undefined
}
type AccountBreakLimit = {
    alert: number
    warn: number
}

export const NONE_SOUND_ID = 1

/* Defaults */
export const NONE_SOUND: UserRecord = {
    Description: 'None',
    RecordID: NONE_SOUND_ID,
    Rec_Path: '',
    User_ID: -1,
    user_rec_status: -1,
    user_rec_status_name: '',
    user_rec_status_name_en: ''
}
export const DEFAULT_SOUND: UserRecord = {
    Description: 'Default Warning',
    RecordID: 2,
    Rec_Path: '',
    User_ID: -1,
    user_rec_status: -1,
    user_rec_status_name: '',
    user_rec_status_name_en: ''
}

export const DEFAULT_WARNING_SOUND = {
    ...DEFAULT_SOUND,
    Rec_Path: '/sound/default-warning.mp3',
}

export const DEFAULT_ERROR_SOUND = {
    ...DEFAULT_SOUND,
    Description: 'Default Error',
    Rec_Path: '/sound/default-error.mp3',
}
export const DEFAULT_WARNING_SOUNDS: Array<UserRecord> = [
    NONE_SOUND,
    DEFAULT_WARNING_SOUND

]
export const DEFAULT_ERROR_SOUNDS: Array<UserRecord> = [
    NONE_SOUND,
    DEFAULT_ERROR_SOUND
]
export const DEFAULT_ALL_SOUNDS: Array<UserRecord> = [
    NONE_SOUND,
    DEFAULT_WARNING_SOUND,
    DEFAULT_ERROR_SOUND
]

/* Protected Data */
const entitiesState = useTabStorage<EntitiesStoreType>(ENTITIES_STORE_KEY, {
    selectedAccountID: undefined
})

/* Computed */
export const selectedAccountID = computed(() => {
    return entitiesState.value.selectedAccountID
})
export const selectedAccountData = computed(() => {
    return EntitiesDataService.dataRef.value.Accounts.find(acc => acc.AccountID === entitiesState.value.selectedAccountID)
})
export const selectedAccountBreaks = computed(() => {
    return selectedAccountData.value?.AccountBreaks ?? []
})

const selectedAccountBreaksById = computed(() => {
    const map = new Map<number, AccountBreak>()
    for (const status of selectedAccountBreaks.value) {
        map.set(status.StatusID, status)
    }
    return map
})
export const entitiesData = computed(() => {
    return EntitiesDataService.dataRef.value
})

export const selectedAccountEntitiesData = computed(() => {
    if (!selectedAccountID?.value) return null
    return SelectedAccountDataEntitiesService.dataRef.value?.[selectedAccountID.value]
})

export const entitiesUsersMapped = computed(() =>
    mapByEntityConfig<UserEntity>(
        entitiesData.value.Users,
        ENTITIES_CONFIG.Users
    )
)

export const entitiesQueuesMapped = computed(() =>
    mapByEntityConfig<QueueEntity>(
        entitiesData.value.Queues,
        ENTITIES_CONFIG.Queues
    )
)

export const entitiesExtensionsMapped = computed(() =>
    mapByEntityConfig<ExtensionEntity>(
        entitiesData.value.Extension,
        ENTITIES_CONFIG.Extension
    )
)

export const entitiesVoiceBotsMapped = computed(() =>
    mapByEntityConfig<VoiceBotEntity>(
        entitiesData.value.VoiceBotList,
        ENTITIES_CONFIG.VoiceBotList
    )
)

export const entitiesCampaignsMapped = computed(() =>
    mapByEntityConfig<CampaignEntity>(
        entitiesData.value.Campaigns,
        ENTITIES_CONFIG.Campaigns
    )
)

export const selectedAccountEntitiesExtensions = computed(() => {
    return selectedAccountEntitiesData.value ?
        mapByEntityConfig<ExtensionEntity>(
            selectedAccountEntitiesData.value.Extension,
            ENTITIES_CONFIG.Extension
        ): {}
})

export const selectedAccountEntitiesUsers = computed(() => {
    return selectedAccountEntitiesData.value ?
        mapByEntityConfig<UserEntity>(
            selectedAccountEntitiesData.value.Users,
            ENTITIES_CONFIG.Users
        ): {}
})

export const selectedAccountEntitiesQueues = computed(() => {
    return selectedAccountEntitiesData.value ?
        mapByEntityConfig<QueueEntity>(
            selectedAccountEntitiesData.value.Queues,
            ENTITIES_CONFIG.Queues
        ): {}
})

export const selectedAccountEntitiesCampaigns = computed(() => {
    return selectedAccountEntitiesData.value ?
        mapByEntityConfig<CampaignEntity>(
            selectedAccountEntitiesData.value?.Campaigns,
            ENTITIES_CONFIG.Campaigns
        ): {}
})

export const selectedAccountEntitiesVoiceBots = computed(() => {
    return selectedAccountEntitiesData.value ?
        mapByEntityConfig<VoiceBotEntity>(
            selectedAccountEntitiesData.value?.VoiceBotList,
            ENTITIES_CONFIG.VoiceBotList
        ): {}
})

export const currentAccountEntitiesExtensions = computed(() => {
    if (ActiveDashboardDataConfig.value?.getEntitiesFromSelectedAccount) {
        return selectedAccountEntitiesExtensions.value
    }
    return entitiesExtensionsMapped.value
})

export const currentAccountEntitiesUsers = computed(() => {
    if (ActiveDashboardDataConfig.value?.getEntitiesFromSelectedAccount) {
        return selectedAccountEntitiesUsers.value
    }
    return entitiesUsersMapped.value
})

export const currentAccountEntitiesQueues = computed(() => {
    if (ActiveDashboardDataConfig.value?.getEntitiesFromSelectedAccount) {
        return selectedAccountEntitiesQueues.value
    }
    return entitiesQueuesMapped.value
})

export const currentAccountEntitiesCampaigns = computed(() => {
    if (ActiveDashboardDataConfig.value?.getEntitiesFromSelectedAccount) {
        return selectedAccountEntitiesCampaigns.value
    }
    return entitiesCampaignsMapped.value
})

export const currentAccountEntitiesVoiceBots = computed(() => {
    if (ActiveDashboardDataConfig.value?.getEntitiesFromSelectedAccount) {
        return selectedAccountEntitiesVoiceBots.value
    }
    return entitiesVoiceBotsMapped.value
})
export const soundList = computed(() => {
    return uniqBy(
        [ ...entitiesData.value.UserRecords ]
            .sort((a, b) => a.Description.localeCompare(b.Description)),
        'Description'
    )
})
export const warningSoundList = computed(() => {
    return [
        ...DEFAULT_WARNING_SOUNDS,
        ...soundList.value
    ]
})
export const errorSoundList = computed(() => {
    return [
        ...DEFAULT_ERROR_SOUNDS,
        ...soundList.value
    ]
})
export const allSoundList = computed(() => {
    return [
        ...DEFAULT_ALL_SOUNDS,
        ...soundList.value
    ]
})

/* Methods */
function updateState<T extends keyof EntitiesStoreType> (key: T, value: EntitiesStoreType[T]) {
    entitiesState.value = {
        ...entitiesState.value,
        [key]: value
    }
}
function isStatusInBreaks (statusId: number): boolean {
    return statusId in statusTypes.value
}
function getAccountData (accountId: number) {
    return EntitiesDataService.data.Accounts?.find(account => account.AccountID === accountId)
}
function getAccountBreaks (accountId: number) {
    return getAccountData(accountId)?.AccountBreaks || []
}
function getStatusById (statusId: number) {
    if (isStatusInBreaks(statusId)) {
        return statusTypes.value[statusId]
    }
}

/* ---PUBLIC--- */
export function clearEntitiesState () {
    entitiesState.value = {
        selectedAccountID: undefined
    }
}

export async function refreshEntitiesData () {
    await GlobalCachedDataService.loadServiceData('EntitiesDataService')
}

export function getEntityList<T extends keyof EntityListData> (entity: T): EntityListData[T] {
    if(!ActiveDashboardDataConfig.value?.getEntitiesFromSelectedAccount) {
        return getBaseEntityList(entity)
    }
    return getSelectedOnlyEntityList(entity)
}

export function getBaseEntityList <T extends keyof EntityListData> (entity: T): EntityListData[T] {
    return EntitiesDataService.data[entity]
}

export function getSelectedOnlyEntityList <T extends keyof EntityListData> (entity: T): EntityListData[T] {
    if (!selectedAccountEntitiesData.value) {
        return []
    }
    return selectedAccountEntitiesData.value[entity] ?? []
}

export function mapByEntityConfig<T> (
    list: T[] | undefined,
    config: { idKey: keyof T }
): Record<number, T> {
    if (!list?.length) return {}

    return list.reduce<Record<number, T>>((acc, item) => {
        acc[Number(item[config.idKey])] = item
        return acc
    }, {})
}


export async function getRemoteEntityList (url: string, expirationTime = DEFAULT_EXPIRATION_CACHE) {
    const cacheData = await IndexDatabaseService.getDatabaseStore()
    const serviceInCache = cacheData.find(i => i.key === url)
    const expired = serviceInCache?.expired || 0
    if (!serviceInCache || (expired) < Date.now()) {
        const response = await entitiesApi.getListByPath(url)
        await IndexDatabaseService.setObjectToStore({
            key: url,
            data: response,
            expired: Date.now() + expirationTime
        })
        return response
    }
    return serviceInCache.data || []
}

export function getSelectedAccountStatusById (statusId: number) {
    return selectedAccountBreaksById.value.get(statusId)
}
export function getSelectedAccountStatusTextById (statusId: number) {
    return getSelectedAccountStatusById(statusId)?.Name || getStatusById(statusId)?.text || 'other'
}
export function getEntityAccountStatusById (accountId: number, statusId: number) {
    const statusInEntities = getAccountBreaks(accountId).find(status => status.StatusID === statusId)
    return statusInEntities || getSelectedAccountStatusById(statusId)
}
export function getAccountBreakLimitByStatusId (accountId: number, statusId: number): AccountBreakLimit {
    const statusData = getEntityAccountStatusById(accountId, statusId)

    return {
        alert: statusData?.AlertSecLimit || 1800,
        warn: statusData?.WarningSecLimit || 60
    }
}

/**
 * Temporary function, because previously we were saving sounds using the description property and now we use the RecordID
 */
export function getSoundObject(
    options: Array<UserRecord>,
    value: string | number
): UserRecord | null
export function getSoundObject<F>(
    options: Array<UserRecord>,
    value: string | number,
    fallback: F
): UserRecord | F
export function getSoundObject (options: Array<UserRecord>, value: string | number, fallback: unknown = null): UserRecord | unknown {
    if (typeof value === 'number') {
        return options.find(option => option.RecordID === value) ?? fallback
    } else if (value === 'default-warning' || value === 'default-error') {
        return options.find(option => option.RecordID === DEFAULT_SOUND.RecordID) ?? fallback
    } else {
        return options.find(option => option.Description === value || option.RecordID === Number(value)) ?? fallback
    }
}

/**
 * This function is used to select account by ID
 * The logic is following:
 * 1. If accountId is not provided, then we want to set either currently selected account (saved in store), or first one
 * 2. If accountId is provided, then we want to set specific account
 * 3. If account we want to set does not exist, then we want to check if there will be account after data load
 * 4. If there will be account after data load, then we want to set first account
 * 5. If there will be no account after data load, then we want to redirect to login
 *
 * @param accountId
 */
export async function selectAccount (accountId?: number) {
    let accountIdToSet: undefined | number = undefined
    const currentAccountId = entitiesState.value.selectedAccountID

    if (accountId === undefined) {
        logger.log('WANT TO SET EITHER CURRENT EITHER FIRST')

        // To get either currently selected account data, or first one
        accountIdToSet = currentAccountId ?? EntitiesDataService.data.Accounts[0]?.AccountID
    } else {
        logger.log('WANT TO SET SPECIFIC')

        // Check if account we want to set exists
        accountIdToSet = EntitiesDataService.data.Accounts.find(account => account.AccountID === accountId)?.AccountID
    }

    if (accountIdToSet === undefined) {
        logger.log('HAVE NO ACCOUNTS TO SET, CHECKING IF THERE WILL BE ACCOUNT AFTER DATA LOAD')

        if (!GlobalCachedDataService.isAllAPICallsFulfilled.value) {
            await new Promise((res) => {
                GlobalCachedDataService.onAllDataRefreshed(res)
            })
        }

        const firstAccountIdAfterDataLoad = EntitiesDataService.data.Accounts[0]?.AccountID

        if (firstAccountIdAfterDataLoad === undefined) {
            logger.log('AFTER DATA LOAD NO ACCOUNTS FOUND')

            redirectToLogin()
        } else {
            logger.log('AFTER DATA LOAD SETTING FIRST ACCOUNT')
            updateState('selectedAccountID', firstAccountIdAfterDataLoad)
        }
    } else {
        logger.log('SETTING ACCOUNT')

        updateState('selectedAccountID', accountIdToSet)
    }
}
